package org.teddapp.project.sync

import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.webSocket
import io.ktor.websocket.Frame
import io.ktor.websocket.readText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.teddapp.project.server.ServerSession

/**
 * La sonnette du serveur : un WebSocket sur `/v1/ws`. Le serveur y envoie `bell` dès
 * qu'un autre appareil du même compte a écrit ; on tire alors tout de suite, au lieu
 * d'attendre le prochain battement. Elle ne transporte aucune donnée.
 *
 * Elle se reconnecte seule, avec une attente qui double jusqu'à une minute, et renvoie
 * son jeton toutes les dix minutes, avant qu'il n'expire.
 */
class ServerBell(
    private val http: HttpClient,
    private val session: ServerSession,
    private val scope: CoroutineScope,
    private val onBell: suspend () -> Unit,
) {
    private val _live = MutableStateFlow(false)
    /** Vrai tant que la sonnette est branchée : les autres boucles peuvent se reposer sur elle. */
    val live: StateFlow<Boolean> = _live
    private var job: Job? = null

    fun start() {
        if (job != null) return
        job = scope.launch { loop() }
    }

    fun stop() {
        job?.cancel(); job = null
        _live.value = false
    }

    private fun wsUrl(): String {
        val base = session.url.trimEnd('/')
        val scheme = if (base.startsWith("https://")) "wss://" else "ws://"
        return scheme + base.removePrefix("https://").removePrefix("http://") + "/v1/ws"
    }

    private suspend fun loop() {
        var wait = RETRY_MIN_MS
        while (scope.isActive) {
            try {
                listen()
                wait = RETRY_MIN_MS
            } catch (e: kotlinx.coroutines.CancellationException) {
                throw e
            } catch (_: Throwable) {
                // Réseau absent, serveur relancé, jeton refusé : on réessaie plus tard.
            } finally {
                _live.value = false
            }
            delay(wait)
            wait = (wait * 2).coerceAtMost(RETRY_MAX_MS)
        }
    }

    private suspend fun listen() {
        http.webSocket(wsUrl()) {
            send(Frame.Text(authMessage()))
            // Le jeton d'accès dure un quart d'heure : on le renvoie avant.
            val renew = launch {
                while (isActive) { delay(REAUTH_MS); send(Frame.Text(authMessage())) }
            }
            try {
                // Une connexion peut mourir sans bruit (réseau coupé, proxy). Sans trame
                // pendant trop longtemps, on la considère morte et on recommence.
                while (true) {
                    val frame = withTimeoutOrNull(SILENCE_MAX_MS) { incoming.receiveCatching().getOrNull() } ?: break
                    val text = (frame as? Frame.Text)?.readText() ?: continue
                    val type = runCatching { json.parseToJsonElement(text).jsonObject["type"]?.jsonPrimitive?.content }.getOrNull()
                    when (type) {
                        "ready" -> _live.value = true
                        "bell" -> onBell()
                        "error" -> break
                    }
                }
            } finally {
                renew.cancel()
            }
        }
    }

    private suspend fun authMessage(): String =
        buildJsonObject { put("type", "auth"); put("token", session.accessToken()) }.toString()

    private companion object {
        val json = Json { ignoreUnknownKeys = true }
        const val RETRY_MIN_MS = 2_000L
        const val RETRY_MAX_MS = 60_000L
        const val REAUTH_MS = 10 * 60_000L
        /** Le serveur répond « ready » à chaque renvoi du jeton : douze minutes sans texte, la connexion est morte. */
        const val SILENCE_MAX_MS = REAUTH_MS + 2 * 60_000L
    }
}
